import { Stack } from '@mui/material';
import { HTMLAttributes } from 'react';
import * as React from 'react';

import { COLORS } from '@/styles/tokens/colors';

import { useTabsContext } from './tabs.context';

export type TabsProps = React.PropsWithChildren<{
  fullWidth?: boolean;
}>;

export function Tabs({ children, fullWidth }: TabsProps) {
  return (
    <Stack
      component="ul"
      direction="row"
      spacing={2}
      sx={{
        listStyleType: 'none',
        p: 0,
        m: 0,
        minHeight: '32px',
        height: 'fit-content',
        borderBottom: `solid ${COLORS.GRAY[200]} 1px`,
        alignItems: 'flex-end',
      }}
    >
      {React.Children.map(children, (child, i) => {
        return (
          <li
            key={i}
            style={{
              display: 'flex',
              height: '100%',
              // Add a negative margin to the bottom of each tab so that the border
              // of the tabs is flush with the border of the container
              marginBottom: '-1px',
              alignItems: 'flex-end',
              flexGrow: fullWidth ? 1 : 0,
            }}
          >
            {child}
          </li>
        );
      })}
    </Stack>
  );
}

export interface TabContentProps<T extends string>
  extends HTMLAttributes<HTMLDivElement> {
  tabKey: T;
  /**
   * When true, unmounts the tab content when inactive instead of hiding with display:none.
   * Use this for tabs with heavy content (e.g., D3 graphs, PDF viewers) to prevent memory leaks.
   * @default false
   */
  unmountWhenInactive?: boolean;
}

export function TabContent<T extends string>({
  children,
  tabKey,
  unmountWhenInactive = false,
  ...props
}: TabContentProps<T>) {
  const { currentTab } = useTabsContext();

  const isActive = currentTab === tabKey;

  if (unmountWhenInactive && !isActive) {
    return null;
  }

  return (
    <div
      style={{
        // Doing this so the contents of each tab don't get unmounted
        display: isActive ? 'unset' : 'none',
      }}
      {...props}
    >
      {children}
    </div>
  );
}
